import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Archive, ArrowLeft, CheckCircle, Clock, Copy, FlaskConical, Pause, Play, RotateCcw, SkipForward, Trash2, XCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CampaignStatus } from "@/components/StatusPill";
import LeadTypePill from "@/components/LeadTypePill";
import { ChannelStatsModule } from "@/components/ChannelStatsModule";
import { getCampaignDisplay, getCampaignETA } from "@/lib/campaignStatus";
import StatusPill from "@/components/StatusPill";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CampaignDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [types, setTypes] = useState([]);
    const [settings, setSettings] = useState(null);
    const [smsTemplate, setSmsTemplate] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [testBatchOpen, setTestBatchOpen] = useState(false);
    const [testBatchSize, setTestBatchSize] = useState(3);
    const [testBatchRunning, setTestBatchRunning] = useState(false);
    const [testBatchResult, setTestBatchResult] = useState(null);
    const [liveWarnOpen, setLiveWarnOpen] = useState(false);
    const load = async () => {
        const { data } = await api.get(`/campaigns/${id}`);
        setD(data);
        setTestBatchSize(prev => prev || data?.campaign?.test_batch_size || 3);
        // resolve sms template (lookup by id OR template_key)
        const tplId = data?.campaign?.sms_template_id;
        if (tplId) {
            try {
                const { data: tpl } = await api.get(`/message-templates/${tplId}`);
                setSmsTemplate(tpl);
            } catch { setSmsTemplate(null); }
        } else {
            setSmsTemplate(null);
        }
    };

    const reloadSettings = async () => {
        const { data } = await api.get("/settings");
        setSettings(data);
        return data;
    };

    useEffect(() => {
        load();
        Promise.all([api.get("/lead-types"), api.get("/settings")])
            .then(([t, s]) => { setTypes(t.data); setSettings(s.data); setTestBatchSize(s.data ? (s.data.test_batch_size || 3) : 3); });
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
        /* eslint-disable-next-line */
    }, [id]);

    const transition = async (action) => {
        try {
            await api.post(`/campaigns/${id}/${action}`);
            toast.success(`Campaign ${action}d`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    // Start button — guards on Test Mode. If LIVE, force a confirmation modal first.
    const onStart = async () => {
        const s = await reloadSettings();
        if (!s?.test_mode) {
            setLiveWarnOpen(true);
            return;
        }
        return transition("start");
    };

    const runTestBatch = async () => {
        try {
            setTestBatchRunning(true);
            setTestBatchResult(null);
            const { data } = await api.post(`/campaigns/${id}/test-batch`, {
                size: Math.max(1, Math.min(50, Number(testBatchSize) || 3)),
            });
            setTestBatchResult(data);
            const acc = data.summary?.accepted ?? 0;
            const fail = data.summary?.failed ?? 0;
            const liveTag = data.live ? "Live" : "Mocked";
            if (acc && !fail) toast.success(`${liveTag}: ${acc} SMS accepted by Twilio`);
            else if (acc && fail) toast.warning(`${liveTag}: ${acc} accepted · ${fail} failed`);
            else if (fail) toast.error(`${liveTag}: ${fail} failed — see results`);
            else toast.info(`${liveTag}: no messages sent`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTestBatchRunning(false);
        }
    };

    const duplicate = async () => {
        try {
            const { data } = await api.post(`/campaigns/${id}/duplicate`);
            toast.success("Campaign duplicated");
            navigate(`/campaigns/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const askDelete = () => {
        setConfirm({
            title: `Delete "${d.campaign.name}"?`,
            body: "This deletes the campaign record. Activity logs and delivery history for this campaign remain in Activity for audit.",
            destructive: true,
            confirmLabel: "Delete",
            run: async () => {
                await api.delete(`/campaigns/${id}`);
                navigate("/campaigns");
            },
        });
    };

    const askArchive = () => {
        setConfirm({
            title: `Archive "${d.campaign.name}"?`,
            body: "Archived campaigns are hidden by default but kept for reporting. Stats are preserved.",
            destructive: false,
            confirmLabel: "Archive",
            run: () => transition("archive"),
        });
    };

    // Send-dialog state — shared for both Start and Restart. `variant`
    // controls the labels + which backend endpoint we hit. modes carry
    // the full set of admin controls (P0.1 defaults preserved).
    const [sendDialog, setSendDialog] = useState(null);

    const openSendDialog = async (variant) => {
        // Fetch preflight so the operator sees eligible/skipped counts
        // BEFORE clicking. Aaron's P0 was made visible by silence; this
        // dialog is the "no more silent completions" contract.
        const modes = {
            resend_mode: "all",
            duplicate_phone_mode: "send_once_per_selected_record",
            internal_do_not_text_mode: "warn_only",
        };
        setSendDialog({ open: true, variant, loading: true, preflight: null, ...modes });
        try {
            const qs = new URLSearchParams(modes).toString();
            const { data } = await api.get(`/campaigns/${id}/preflight?${qs}`);
            setSendDialog(prev => ({ ...(prev || {}), open: true, loading: false, preflight: data }));
        } catch (e) {
            toast.error(formatApiError(e) || "Could not load preflight");
            setSendDialog(null);
        }
    };

    const changeSendMode = async (field, value) => {
        setSendDialog(prev => ({ ...(prev || {}), loading: true, [field]: value }));
        try {
            const next = { ...(sendDialog || {}), [field]: value };
            const qs = new URLSearchParams({
                resend_mode: next.resend_mode,
                duplicate_phone_mode: next.duplicate_phone_mode,
                internal_do_not_text_mode: next.internal_do_not_text_mode,
            }).toString();
            const { data } = await api.get(`/campaigns/${id}/preflight?${qs}`);
            setSendDialog(prev => ({ ...(prev || {}), loading: false, preflight: data }));
        } catch (e) {
            toast.error(formatApiError(e) || "Could not reload preflight");
            setSendDialog(prev => ({ ...(prev || {}), loading: false }));
        }
    };

    const confirmSend = async () => {
        const { variant, resend_mode, duplicate_phone_mode, internal_do_not_text_mode } = sendDialog || {};
        const endpoint = variant === "restart" ? "restart" : "start";
        setSendDialog(prev => ({ ...(prev || {}), submitting: true }));
        try {
            await api.post(`/campaigns/${id}/${endpoint}`, {
                resend_mode, duplicate_phone_mode, internal_do_not_text_mode,
            });
            toast.success(variant === "restart" ? "Campaign restarted" : "Campaign started");
            setSendDialog(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
            setSendDialog(prev => ({ ...(prev || {}), submitting: false }));
        }
    };

    const askRestart = () => openSendDialog("restart");

    // ── Run panel state (Resume Remaining) ────────────────────────────
    const [runPanel, setRunPanel] = useState(null);
    const [runResuming, setRunResuming] = useState(false);

    const loadRunPanel = async () => {
        try {
            const { data } = await api.get(`/campaigns/${id}/runs/latest/audit`);
            setRunPanel(data);
        } catch { setRunPanel(null); }
    };

    useEffect(() => { if (d?.campaign?.id) loadRunPanel(); }, [d?.campaign?.id, d?.campaign?.status]);

    const resumeRemaining = async () => {
        if (!runPanel?.send_job_id) return;
        setRunResuming(true);
        try {
            const { data } = await api.post(
                `/campaigns/${id}/runs/${runPanel.send_job_id}/resume`,
                { include_transient_failures: false },
            );
            toast.success(
                `Resume queued ${data.resumed_count} · recovered ${data.stale_recovered_count} stale · ${data.remaining_count} remaining`,
            );
            await Promise.all([load(), loadRunPanel()]);
        } catch (e) {
            toast.error(formatApiError(e) || "Resume failed");
        } finally {
            setRunResuming(false);
        }
    };

    // Start now opens the same dialog as Restart, so operators see all
    // three admin toggles + a live preflight count before any send job
    // is created. The old "Live Mode warning" AlertDialog is preserved
    // as an additional guardrail when settings.test_mode=false.
    const onStartWithModes = async () => {
        const s = await reloadSettings();
        if (!s?.test_mode) {
            setLiveWarnOpen(true);
            return;
        }
        openSendDialog("start");
    };


    if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
    const c = d.campaign;
    const stats = c.channel_stats || {};
    const showSms = !!stats.sms;
    const showEmail = !!stats.email;
    const dual = showSms && showEmail;
    const dateLabel = {
        all: "All time", today: "Today", "7d": "Last 7 days", "30d": "Last 30 days",
        "60d": "Last 60 days", "90d": "Last 90 days", custom: "Custom range",
    }[c.date_filter_kind || "all"] || "All time";
    const typeObjs = (c.lead_type_ids || []).map(id => types.find(t => t.id === id)).filter(Boolean);
    const display = getCampaignDisplay(c, settings);
    const eta = getCampaignETA(c);
    const sw = c.send_window || { mode: "global" };
    const windowLabel = sw.mode === "24h"
        ? "24 hours"
        : sw.mode === "custom"
            ? `${(sw.days || []).map(d => DAY_LABELS[d]).join(", ")} · ${sw.start || "?"}–${sw.end || "?"}`
            : (settings?.operating_hours
                ? `Global · ${(settings.operating_hours.days || []).map(d => DAY_LABELS[d]).join(", ")} · ${settings.operating_hours.start}–${settings.operating_hours.end}`
                : "Global hours");

    return (
        <div className="space-y-6" data-testid="campaign-detail-page">
            <Link to="/campaigns" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to campaigns
            </Link>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">{c.name}</h1>
                        <CampaignStatus status={c.status} />
                        <StatusPill tone={display.tone} testid="campaign-display-status">{display.label}</StatusPill>
                        {eta && (
                            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="campaign-eta">
                                <Clock className="h-3.5 w-3.5" /> ETA {eta}
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {c.channel === "both" ? "SMS + Email" : c.channel === "sms" ? "SMS" : "Email"} ·
                        {" "}rate {c.send_rate_per_hour}/hr · target {c.target_count?.toLocaleString?.() ?? c.target_count}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {(c.status === "draft" || c.status === "scheduled") && (
                        <Button onClick={onStartWithModes} data-testid="start-campaign-button">
                            <Play className="h-4 w-4 mr-1" /> Start
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => { setTestBatchResult(null); setTestBatchOpen(true); }}
                        data-testid="open-test-batch-button"
                        disabled={!c.lead_type_ids?.length}
                        title={c.lead_type_ids?.length ? "Send to N real targeted leads" : "Add a lead type to enable test batches"}
                    >
                        <FlaskConical className="h-4 w-4 mr-1" /> Run test batch
                    </Button>
                    {c.status === "running" && (
                        <Button variant="outline" onClick={() => transition("pause")} data-testid="pause-campaign-button">
                            <Pause className="h-4 w-4 mr-1" /> Pause
                        </Button>
                    )}
                    {c.status === "paused" && (
                        <Button onClick={() => transition("resume")} data-testid="resume-campaign-button">
                            <Play className="h-4 w-4 mr-1" /> Resume
                        </Button>
                    )}
                    {(c.status === "paused" || c.status === "completed") && (
                        <Button variant="outline" onClick={askRestart} data-testid="restart-campaign-button">
                            <RotateCcw className="h-4 w-4 mr-1" /> Restart
                        </Button>
                    )}
                    <Button variant="outline" onClick={duplicate} data-testid="duplicate-campaign-button">
                        <Copy className="h-4 w-4 mr-1" /> Duplicate
                    </Button>
                    {c.status !== "archived" && (
                        <Button variant="outline" onClick={askArchive} data-testid="archive-campaign-button">
                            <Archive className="h-4 w-4 mr-1" /> Archive
                        </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={askDelete} data-testid="delete-campaign-button" aria-label="Delete campaign" title="Delete campaign">
                        <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                </div>
            </div>

            <section data-testid="channel-performance-section" className="space-y-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-medium text-gray-900">Channel Performance</h2>
                        <p className="text-[11px] text-muted-foreground">
                            Counts from the app's own communications log — provider acceptance only, no delivery webhook yet.
                        </p>
                    </div>
                    {c.status === "paused" && (
                        <div className="text-xs text-amber-700" data-testid="paused-breakdown">
                            Paused · resume to continue sending
                        </div>
                    )}
                </div>
                <div className={`grid gap-4 ${dual ? "lg:grid-cols-2 grid-cols-1" : "grid-cols-1"}`}>
                    {showSms && <ChannelStatsModule channel="sms" stats={stats.sms} />}
                    {showEmail && <ChannelStatsModule channel="email" stats={stats.email} />}
                </div>
                {(c.replies_count || c.opt_out_count) ? (
                    <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground" data-testid="cross-channel-aux">
                        <span><span className="font-medium text-gray-700">{c.replies_count.toLocaleString()}</span> replies</span>
                        <span aria-hidden>·</span>
                        <span><span className="font-medium text-gray-700">{c.opt_out_count.toLocaleString()}</span> opt-outs</span>
                    </div>
                ) : null}
            </section>

            {/* Campaign Run panel — surfaces the CURRENT send job with a
                Resume Remaining action for any campaign that stopped
                partway. Uses /runs/latest/audit as the source of truth
                so cached counters can never lie about progress. */}
            {runPanel?.send_job_id && (
                <section className="bg-white border border-border rounded-xl p-5 shadow-sm space-y-3" data-testid="campaign-run-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h2 className="text-base font-medium text-gray-900">Current run</h2>
                            <p className="text-xs text-muted-foreground">Live counts pulled from the intent table (not cached).</p>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="ghost" onClick={loadRunPanel} data-testid="run-refresh">
                                Refresh
                            </Button>
                            {(() => {
                                const bs = runPanel?.counters?.by_status || {};
                                const remaining = (bs.queued || 0) + (bs.running || 0);
                                if (["completed", "archived", "cancelled", "failed"].includes(c.status)) return null;
                                return (
                                    <Button size="sm" onClick={resumeRemaining}
                                        disabled={runResuming || remaining === 0}
                                        data-testid="run-resume-remaining">
                                        <Play className="h-3.5 w-3.5 mr-1" />
                                        {runResuming ? "Resuming…" : `Resume Remaining (${remaining})`}
                                    </Button>
                                );
                            })()}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-center">
                        {[
                            ["Selected", runPanel?.counters?.total_intents ?? 0, "text-gray-800", "bg-gray-50"],
                            ["Queued", runPanel?.counters?.by_status?.queued || 0, "text-blue-800", "bg-blue-50"],
                            ["Running", runPanel?.counters?.by_status?.running || 0, "text-amber-800", "bg-amber-50"],
                            ["Accepted", runPanel?.counters?.by_status?.accepted || 0, "text-emerald-800", "bg-emerald-50"],
                            ["Failed", runPanel?.counters?.by_status?.failed || 0, "text-rose-800", "bg-rose-50"],
                            ["Skipped", runPanel?.counters?.by_status?.skipped || 0, "text-slate-800", "bg-slate-50"],
                        ].map(([label, n, color, bg]) => (
                            <div key={label} className={`rounded-md border px-2 py-2 ${bg}`}
                                 data-testid={`run-count-${label.toLowerCase()}`}>
                                <div className={`text-[10px] uppercase tracking-wider ${color} opacity-70`}>{label}</div>
                                <div className={`text-lg font-semibold ${color}`}>{n}</div>
                            </div>
                        ))}
                    </div>
                    {runPanel?.delivery && (
                        <div className="border-t border-border pt-2 mt-1" data-testid="run-delivery-section">
                            <div className="text-[11px] font-medium text-gray-700 uppercase tracking-wider mb-1">
                                Twilio Delivery
                                <span className="ml-2 text-[10px] normal-case font-normal text-muted-foreground">
                                    (from status callbacks — separate from execution above)
                                </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-center">
                                {[
                                    ["Sent", runPanel.delivery.sent || 0, "text-blue-800", "bg-blue-50"],
                                    ["Delivered", runPanel.delivery.delivered || 0, "text-emerald-800", "bg-emerald-50"],
                                    ["Undelivered", runPanel.delivery.undelivered || 0, "text-rose-800", "bg-rose-50"],
                                    ["Failed", runPanel.delivery.failed || 0, "text-rose-900", "bg-rose-100"],
                                    ["Canceled", runPanel.delivery.canceled || 0, "text-slate-800", "bg-slate-50"],
                                    ["Awaiting", runPanel.delivery.awaiting_final || 0, "text-amber-800", "bg-amber-50"],
                                ].map(([label, n, color, bg]) => (
                                    <div key={label} className={`rounded-md border px-2 py-2 ${bg}`}
                                         data-testid={`run-delivery-${label.toLowerCase()}`}>
                                        <div className={`text-[10px] uppercase tracking-wider ${color} opacity-70`}>{label}</div>
                                        <div className={`text-lg font-semibold ${color}`}>{n}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                        <span>Job <code className="font-mono text-[10px]">{runPanel.send_job_id.slice(0,8)}…</code></span>
                        <span>Status <span className="font-medium">{runPanel.job?.status}</span></span>
                        {runPanel.job?.sms_route?.route_name && (
                            <span>Route <span className="font-medium">{runPanel.job.sms_route.route_name}</span></span>
                        )}
                        {runPanel.job?.sms_route?.messaging_service_sid_masked && (
                            <span>MG <code className="font-mono text-[10px]">{runPanel.job.sms_route.messaging_service_sid_masked}</code></span>
                        )}
                        {runPanel.job?.started_at && (
                            <span>Started {new Date(runPanel.job.started_at).toLocaleString()}</span>
                        )}
                        {runPanel.job?.completed_at && (
                            <span>Completed {new Date(runPanel.job.completed_at).toLocaleString()}</span>
                        )}
                    </div>
                    {(runPanel.failed_reason_breakdown || []).length > 0 && (
                        <div className="text-xs">
                            <div className="font-medium mb-1">Failure breakdown</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-0.5 text-rose-900">
                                {runPanel.failed_reason_breakdown.map((row, i) => (
                                    <div key={i} className="flex justify-between">
                                        <span title={row.error_code}>{(row.reason || "unknown").replace(/_/g," ")}{row.error_code ? ` (${row.error_code})` : ""}</span>
                                        <span className="font-mono">{row.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                    <h2 className="text-base font-medium text-gray-900 mb-3">Configuration</h2>
                    <dl className="text-sm divide-y divide-border">
                        <Row k="SMS Template" v={smsTemplate ? (
                            <Link to="/templates" className="hover:underline" data-testid="sms-template-link">
                                <code className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded mr-1">{smsTemplate.template_key}</code>
                                {smsTemplate.template_name}
                            </Link>
                        ) : (c.sms_template_id ? <span className="text-rose-600">missing (live block)</span> : "—")} />
                        <Row k="Email Template" v={c.email_template_id || "—"} />
                        <div className="flex items-start justify-between py-2 gap-3">
                            <dt className="text-muted-foreground">Lead Types</dt>
                            <dd className="flex flex-wrap gap-1 justify-end">
                                {typeObjs.length === 0 && <span className="text-gray-500">—</span>}
                                {typeObjs.map(t => <LeadTypePill key={t.id} leadType={t} />)}
                            </dd>
                        </div>
                        <Row k="Date Filter" v={dateLabel} />
                        {c.date_filter_kind === "custom" && (
                            <Row k="Date Range" v={`${c.date_filter_from || "—"} → ${c.date_filter_to || "—"}`} />
                        )}
                        <Row k="Send Window" v={windowLabel} />
                        <Row k="Send Rate" v={`${c.send_rate_per_hour}/hr`} />
                        <Row k="Test Batch Size" v={c.test_batch_size || 3} />
                        <Row k="Scheduled At" v={c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—"} />
                        <Row k="Created" v={new Date(c.created_at).toLocaleString()} />
                    </dl>
                </div>

                <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                    <h2 className="text-base font-medium text-gray-900 mb-3">Live Activity</h2>
                    {d.timeline.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No activity yet.</div>
                    ) : (
                        <ol className="relative border-l border-border ml-2 max-h-96 overflow-y-auto">
                            {d.timeline.map(t => (
                                <li key={t.id} className="ml-4 pb-3 text-sm">
                                    <span className="absolute -left-[5px] mt-1.5 h-2 w-2 rounded-full bg-primary" />
                                    <div className="text-gray-900">
                                        <span className="capitalize">{t.channel}</span> · <span className="capitalize">{t.direction}</span> · <span className="capitalize">{t.status}</span>
                                    </div>
                                    {t.body && <div className="text-xs text-gray-600 mt-0.5">{t.body}</div>}
                                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleTimeString()}</div>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
                <AlertDialogContent data-testid="campaign-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
                        <AlertDialogDescription className="leading-relaxed">{confirm?.body}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={async () => { const fn = confirm?.run; setConfirm(null); if (fn) await fn(); }}
                            className={confirm?.destructive ? "bg-rose-600 hover:bg-rose-700" : ""}
                            data-testid="campaign-confirm-action"
                        >
                            {confirm?.confirmLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Test Batch Dialog */}
            <Dialog open={testBatchOpen} onOpenChange={setTestBatchOpen}>
                <DialogContent className="max-w-3xl" data-testid="test-batch-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FlaskConical className="h-4 w-4 text-amber-600" /> Test batch — {c.name}
                        </DialogTitle>
                        <DialogDescription>
                            Sends to the most recent N targeted leads using this campaign's filter and template.{" "}
                            <span className={settings?.test_mode ? "text-amber-700" : "text-rose-700 font-medium"}>
                                {settings?.test_mode ? "Test Mode is ON — sends are mocked." : "LIVE Mode — real Twilio/SendGrid calls."}
                            </span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex items-end gap-3">
                            <div className="flex-1 max-w-[160px]">
                                <Label>Batch size</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={testBatchSize}
                                    onChange={e => setTestBatchSize(e.target.value)}
                                    data-testid="test-batch-size-dialog-input"
                                />
                            </div>
                            <Button onClick={runTestBatch} disabled={testBatchRunning} data-testid="run-test-batch-button">
                                {testBatchRunning ? "Sending…" : "Send test batch"}
                            </Button>
                        </div>

                        {testBatchResult && (
                            <div className="space-y-3" data-testid="test-batch-result">
                                {/* Summary chips */}
                                <div className="flex gap-2 flex-wrap" data-testid="test-batch-summary">
                                    <div className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs ${testBatchResult.live ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                                        <span className="uppercase tracking-wider">{testBatchResult.live ? "Live" : "Mocked"}</span>
                                    </div>
                                    <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-800">
                                        <CheckCircle className="h-3.5 w-3.5" />
                                        <span><span className="font-display font-semibold tabular">{testBatchResult.summary?.accepted ?? 0}</span> accepted</span>
                                    </div>
                                    {(testBatchResult.summary?.failed ?? 0) > 0 && (
                                        <div className="flex items-center gap-2 rounded-md bg-rose-50 border border-rose-200 px-3 py-1.5 text-xs text-rose-800">
                                            <XCircle className="h-3.5 w-3.5" />
                                            <span><span className="font-display font-semibold tabular">{testBatchResult.summary.failed}</span> failed</span>
                                        </div>
                                    )}
                                    {(testBatchResult.summary?.skipped ?? 0) > 0 && (
                                        <div className="flex items-center gap-2 rounded-md bg-gray-100 border border-border px-3 py-1.5 text-xs text-gray-700">
                                            <SkipForward className="h-3.5 w-3.5" />
                                            <span><span className="font-display font-semibold tabular">{testBatchResult.summary.skipped}</span> skipped</span>
                                        </div>
                                    )}
                                </div>

                                {/* Result rows grouped: accepted → failed → skipped */}
                                <div className="border border-border rounded-md divide-y divide-border max-h-80 overflow-auto">
                                    {[...testBatchResult.results].sort((a, b) => {
                                        const order = { accepted: 0, failed: 1, skipped: 2 };
                                        return (order[a.outcome] ?? 3) - (order[b.outcome] ?? 3);
                                    }).map(r => (
                                        <div key={`${r.lead_id}-${r.channel}`} className="px-3 py-2 flex flex-col gap-1.5" data-testid={`test-batch-row-${r.lead_id}-${r.channel}`}>
                                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                                <OutcomeBadge outcome={r.outcome} status={r.status} />
                                                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.channel === "email" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>{r.channel}</span>
                                                <span className="font-medium text-gray-900">{r.name || "—"}</span>
                                                {r.to && <span className="text-muted-foreground font-mono">{r.to}</span>}
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-auto">
                                                    {r.outcome === "accepted" && r.channel === "sms" && "by Twilio"}
                                                    {r.outcome === "accepted" && r.channel === "email" && "by SendGrid"}
                                                </span>
                                            </div>
                                            {r.rendered_body && (
                                                <div className="text-xs text-gray-800 bg-gray-50 border border-border rounded px-2 py-1 font-mono whitespace-pre-wrap break-words">
                                                    {r.rendered_body}
                                                </div>
                                            )}
                                            {r.outcome !== "accepted" && (r.error || r.error_code) && (
                                                <div className="text-[11px] text-rose-700 flex gap-2 flex-wrap">
                                                    {r.error_code && <code className="font-mono bg-rose-50 px-1.5 py-0.5 rounded">{r.error_code}</code>}
                                                    {r.failed_field && <code className="font-mono bg-rose-50 px-1.5 py-0.5 rounded">field: {r.failed_field}</code>}
                                                    {r.error && <span>{r.error}</span>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTestBatchOpen(false)} data-testid="close-test-batch">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Live Mode warning before starting the campaign */}
            <AlertDialog open={liveWarnOpen} onOpenChange={setLiveWarnOpen}>
                <AlertDialogContent data-testid="live-mode-warning-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
                            <Zap className="h-4 w-4" /> LIVE Mode — confirm campaign launch
                        </AlertDialogTitle>
                        <AlertDialogDescription className="leading-relaxed">
                            Test Mode is currently <strong>OFF</strong>. Starting this campaign will fire <strong>real Twilio/SendGrid API calls</strong> at up to{" "}
                            <span className="font-mono">{c.send_rate_per_hour}/hr</span> against{" "}
                            <span className="font-mono">{c.target_count?.toLocaleString?.() ?? c.target_count}</span> targeted leads.
                            <br /><br />
                            We recommend running a Test Batch first. Once started, Live messages cannot be unsent.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="live-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-rose-600 hover:bg-rose-700"
                            data-testid="live-confirm-start"
                            onClick={async () => { setLiveWarnOpen(false); openSendDialog("start"); }}
                        >
                            Launch Live campaign
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Unified Start/Restart dialog — exposes all 3 admin send
                modes + live preflight so Aaron can see exactly who will
                be attempted, warned, and skipped BEFORE any send job is
                created. Backend defaults are preserved verbatim. */}
            <Dialog open={!!sendDialog?.open}
                    onOpenChange={(o) => { if (!o) setSendDialog(null); }}>
                <DialogContent className="max-w-lg" data-testid="send-preflight-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {sendDialog?.variant === "restart" ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                            {sendDialog?.variant === "restart" ? `Restart “${c.name}”?` : `Start “${c.name}”?`}
                        </DialogTitle>
                        <DialogDescription>
                            {sendDialog?.variant === "restart"
                                ? "A new send job will be created. Previous send history is preserved."
                                : "A new send job will be created for this campaign. You can review counts and mode selections before confirming."}
                        </DialogDescription>
                    </DialogHeader>
                    {sendDialog?.loading && (
                        <div className="py-6 text-sm text-muted-foreground">
                            Calculating preflight…
                        </div>
                    )}
                    {!sendDialog?.loading && sendDialog?.preflight && (
                        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                            {sendDialog.preflight.prior_run && (
                                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs" data-testid="restart-prior-run">
                                    <div className="font-medium mb-1">Previous run</div>
                                    <div className="flex gap-3 text-muted-foreground">
                                        <span>Accepted <span className="font-semibold text-emerald-700">{sendDialog.preflight.prior_run.accepted_count}</span></span>
                                        <span>Failed <span className="font-semibold text-rose-700">{sendDialog.preflight.prior_run.failed_count}</span></span>
                                        <span>Skipped <span className="font-semibold text-slate-700">{sendDialog.preflight.prior_run.skipped_count}</span></span>
                                    </div>
                                </div>
                            )}
                            {sendDialog.preflight.sms_route && (
                                <div
                                    className={`rounded-md border px-3 py-2 text-xs ${sendDialog.preflight.sms_route.ok ? (sendDialog.preflight.sms_route.route_source === "legacy_global" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50/60") : "border-rose-400 bg-rose-50"}`}
                                    data-testid="restart-sms-route"
                                    data-route-source={sendDialog.preflight.sms_route.route_source}
                                    data-route-ok={String(!!sendDialog.preflight.sms_route.ok)}
                                >
                                    <div className="font-medium mb-1">SMS Twilio route</div>
                                    {sendDialog.preflight.sms_route.ok ? (
                                        <>
                                            <div className="flex justify-between">
                                                <span>Route</span>
                                                <span className="font-semibold" data-testid="restart-sms-route-name">
                                                    {sendDialog.preflight.sms_route.route_name || "— (legacy global)"}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Messaging Service SID</span>
                                                <span className="font-mono" data-testid="restart-sms-route-mg">
                                                    {sendDialog.preflight.sms_route.messaging_service_sid_masked
                                                     || (sendDialog.preflight.sms_route.twilio_from_number
                                                         ? `from ${sendDialog.preflight.sms_route.twilio_from_number}` : "—")}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-muted-foreground">
                                                <span>Source</span>
                                                <span>{(sendDialog.preflight.sms_route.route_source || "").replace(/_/g," ")}</span>
                                            </div>
                                            {sendDialog.preflight.sms_route.route_source === "legacy_global" && (
                                                <div className="mt-1 text-amber-900">
                                                    Using the legacy global Twilio route because the selected template has no provider route.
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-rose-900">
                                            <div className="font-semibold">{sendDialog.preflight.sms_route.error_reason}</div>
                                            <div>{sendDialog.preflight.sms_route.error_message}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Send Behavior */}
                            <fieldset className="space-y-2 rounded-md border px-3 py-2" data-testid="send-behavior-fieldset">
                                <legend className="text-xs font-medium text-gray-700 uppercase tracking-wider px-1">
                                    Send Behavior
                                </legend>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="resend_mode"
                                        checked={sendDialog.resend_mode === "all"}
                                        onChange={() => changeSendMode("resend_mode", "all")}
                                        className="mt-1" data-testid="mode-resend-all" />
                                    <div>
                                        <div>Send again to selected recipients</div>
                                        <div className="text-xs text-muted-foreground">Everyone in the audience is queued — including recipients who received a prior message.</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="resend_mode"
                                        checked={sendDialog.resend_mode === "not_accepted"}
                                        onChange={() => changeSendMode("resend_mode", "not_accepted")}
                                        className="mt-1" data-testid="mode-resend-not-accepted" />
                                    <div>
                                        <div>Skip recipients already accepted by Twilio in this campaign</div>
                                        <div className="text-xs text-muted-foreground">Skips recipients whose message was provider-accepted in any prior send job.</div>
                                    </div>
                                </label>
                            </fieldset>

                            {/* Duplicate Phone Behavior */}
                            <fieldset className="space-y-2 rounded-md border px-3 py-2" data-testid="dup-phone-fieldset">
                                <legend className="text-xs font-medium text-gray-700 uppercase tracking-wider px-1">
                                    Duplicate Phone Behavior
                                </legend>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="duplicate_phone_mode"
                                        checked={sendDialog.duplicate_phone_mode === "send_once_per_selected_record"}
                                        onChange={() => changeSendMode("duplicate_phone_mode", "send_once_per_selected_record")}
                                        className="mt-1" data-testid="mode-dup-per-record" />
                                    <div>
                                        <div>Send once per selected record</div>
                                        <div className="text-xs text-muted-foreground">Two records with the same phone both get queued (with a warning).</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="duplicate_phone_mode"
                                        checked={sendDialog.duplicate_phone_mode === "send_once_per_unique_phone"}
                                        onChange={() => changeSendMode("duplicate_phone_mode", "send_once_per_unique_phone")}
                                        className="mt-1" data-testid="mode-dup-unique-phone" />
                                    <div>
                                        <div>Send once per unique phone number</div>
                                        <div className="text-xs text-muted-foreground">Duplicate phones after the first are visibly skipped.</div>
                                    </div>
                                </label>
                            </fieldset>

                            {/* Internal Do Not Text */}
                            <fieldset className="space-y-2 rounded-md border px-3 py-2" data-testid="dnt-fieldset">
                                <legend className="text-xs font-medium text-gray-700 uppercase tracking-wider px-1">
                                    Internal Do Not Text
                                </legend>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="internal_do_not_text_mode"
                                        checked={sendDialog.internal_do_not_text_mode === "warn_only"}
                                        onChange={() => changeSendMode("internal_do_not_text_mode", "warn_only")}
                                        className="mt-1" data-testid="mode-dnt-warn" />
                                    <div>
                                        <div>Warn and send</div>
                                        <div className="text-xs text-muted-foreground">Recipients marked do_not_text are queued with a visible warning.</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="internal_do_not_text_mode"
                                        checked={sendDialog.internal_do_not_text_mode === "block"}
                                        onChange={() => changeSendMode("internal_do_not_text_mode", "block")}
                                        className="mt-1" data-testid="mode-dnt-block" />
                                    <div>
                                        <div>Block</div>
                                        <div className="text-xs text-muted-foreground">Recipients marked do_not_text are visibly skipped.</div>
                                    </div>
                                </label>
                            </fieldset>

                            <div className="grid grid-cols-3 gap-2 text-center" data-testid="restart-preflight-counts">
                                <div className="rounded-md border bg-primary/5 px-2 py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Selected</div>
                                    <div className="text-lg font-semibold" data-testid="pf-selected">{sendDialog.preflight.selected_count}</div>
                                </div>
                                <div className="rounded-md border bg-emerald-50 px-2 py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-emerald-800">Will attempt</div>
                                    <div className="text-lg font-semibold text-emerald-800" data-testid="pf-eligible">{sendDialog.preflight.eligible_count}</div>
                                </div>
                                <div className="rounded-md border bg-slate-50 px-2 py-2">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-700">Skipped</div>
                                    <div className="text-lg font-semibold text-slate-700" data-testid="pf-skipped">{sendDialog.preflight.skipped_count}</div>
                                </div>
                            </div>
                            {Object.keys(sendDialog.preflight.skip_breakdown || {}).length > 0 && (
                                <div className="text-xs" data-testid="restart-skip-breakdown">
                                    <div className="font-medium mb-1">Hard-block reasons</div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                                        {Object.entries(sendDialog.preflight.skip_breakdown).map(([reason, n]) => (
                                            <div key={reason} className="flex justify-between">
                                                <span>{reason.replace(/_/g, " ")}</span>
                                                <span className="font-mono">{n}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {Object.keys(sendDialog.preflight.warning_breakdown || {}).length > 0 && (
                                <div className="text-xs" data-testid="pf-warning-breakdown">
                                    <div className="font-medium mb-1">Warnings (queued anyway)</div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-amber-900">
                                        {Object.entries(sendDialog.preflight.warning_breakdown).map(([reason, n]) => (
                                            <div key={reason} className="flex justify-between">
                                                <span>{reason.replace(/_/g, " ")}</span>
                                                <span className="font-mono">{n}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(sendDialog.preflight.recent_contact_warning?.recipient_count || 0) > 0 && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="restart-recent-warning">
                                    <strong>Heads up:</strong> {sendDialog.preflight.recent_contact_warning.recipient_count} recipient(s) in this audience received an SMS in the last {sendDialog.preflight.recent_contact_warning.hours}h.
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setSendDialog(null)}
                            disabled={sendDialog?.submitting}
                            data-testid="restart-cancel"
                        >Cancel</Button>
                        <Button onClick={confirmSend}
                            disabled={
                                sendDialog?.submitting
                                || sendDialog?.loading
                                || (sendDialog?.preflight?.eligible_count ?? 0) === 0
                                || (sendDialog?.preflight?.sms_route
                                    && sendDialog.preflight.sms_route.ok === false)
                            }
                            data-testid="restart-confirm"
                        >
                            {sendDialog?.submitting
                                ? (sendDialog.variant === "restart" ? "Restarting…" : "Starting…")
                                : (sendDialog?.variant === "restart" ? "Restart" : "Start")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-center justify-between py-2">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-gray-900">{v}</dd>
        </div>
    );
}

const OUTCOME_LABEL = {
    queued: "Accepted by Twilio",
    accepted: "Accepted",
    sending: "Sending",
    sent: "Sent",
    delivered: "Delivered",
    failed: "Failed",
    skipped: "Skipped",
    opted_out: "Opted out",
};

function OutcomeBadge({ outcome, status }) {
    const label = OUTCOME_LABEL[status] || status;
    const cls =
        outcome === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
        outcome === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" :
        outcome === "skipped" ? "bg-gray-100 text-gray-700 border-border" :
        "bg-gray-100 text-gray-700 border-border";
    const Icon =
        outcome === "accepted" ? CheckCircle :
        outcome === "failed" ? XCircle :
        SkipForward;
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider ${cls}`}>
            <Icon className="h-3 w-3" /> {label}
        </span>
    );
}
